import { useEffect, useMemo, useRef, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Tooltip } from "primereact/tooltip";
import DashboardServices, {
  AttendantProductivity,
  AttendantsProductivityResponse,
  DashboardAttendantOption
} from "../../../../services/dashboardService";
import { Text } from "../../../../components/Text";
import {
  Section,
  Header,
  HeaderTitle,
  TitleIcon,
  HeaderActions,
  SortButton,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  InfoIcon,
  Chart,
  Legend,
  LegendColor,
  ChartRow,
  BarTrack,
  BarSegment,
  TableWrapper,
  NumberCell,
  AttendantCell,
  Avatar,
  RateCell,
  RateTrack,
  RateBar,
  Footnote
} from "./styles";

interface IAttendantsProductivityProps {
  bots: number[];
  startAt: string;
  endAt: string;
  reloadKey: number;
}

type SortType = "closed" | "inProgress";

const CLOSED_COLOR = "#046AF3";
const IN_PROGRESS_COLOR = "#FF9F43";
const CLOSED_IN_PERIOD_TOOLTIP =
  "Conta pela data de encerramento, por isso pode ser diferente de “Atendimentos finalizados”, que considera a data de início. Considera apenas atendimentos que tiveram atendente. Sem filtro de atendente, inclui os encerrados automaticamente (linha “Sistema”).";

const formatNumber = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : value.toLocaleString("pt-BR");

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "?";
  }
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

const AttendantsProductivity: React.FC<IAttendantsProductivityProps> = ({
  bots,
  startAt,
  endAt,
  reloadKey
}) => {
  const { getAttendantsProductivity, getDashboardAttendants } =
    DashboardServices();
  const [attendantOptions, setAttendantOptions] = useState<
    DashboardAttendantOption[]
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [sort, setSort] = useState<SortType>("closed");
  const [data, setData] = useState<AttendantsProductivityResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const lastRequest = useRef(0);
  const botsKey = bots.join(",");

  const sortedRows = useMemo(() => {
    const rows = [...(data?.attendants ?? [])];
    const value = (row: AttendantProductivity) =>
      (sort === "closed" ? row.closed : row.inProgress) ?? 0;

    return rows.sort((a, b) => {
      if (a.system !== b.system) {
        return a.system ? 1 : -1;
      }
      return value(b) - value(a) || a.name.localeCompare(b.name, "pt-BR");
    });
  }, [data, sort]);

  const chartRows = sortedRows.filter((row) => !row.system);
  const maxTotal = Math.max(
    0,
    ...chartRows.map((row) => row.closed + (row.inProgress ?? 0))
  );

  const options = attendantOptions.map((option) => ({
    id: option.id,
    label: option.active ? option.name : `${option.name} (inativo)`
  }));

  const loadAttendants = async () => {
    if (!bots.length) {
      setAttendantOptions([]);
      return;
    }

    try {
      const result = await getDashboardAttendants({ bots });
      const ids = new Set(result.map((option) => option.id));
      setAttendantOptions(result);
      setSelectedUsers((current) => {
        const kept = current.filter((id) => ids.has(id));
        return kept.length === current.length ? current : kept;
      });
    } catch (error) {
      // useFetch2 já exibe o toast de erro
    }
  };

  const loadProductivity = async () => {
    if (!reloadKey || !bots.length) {
      return;
    }

    const request = ++lastRequest.current;
    setLoading(true);

    try {
      const result = await getAttendantsProductivity({
        bots,
        startAt,
        endAt,
        users: selectedUsers
      });
      if (request === lastRequest.current) {
        setData(result);
      }
    } catch (error) {
      // useFetch2 já exibe o toast de erro
    } finally {
      if (request === lastRequest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAttendants();
  }, [botsKey]);

  useEffect(() => {
    loadProductivity();
  }, [reloadKey, selectedUsers]);

  const summary = data?.summary;

  return (
    <Section dimmed={loading} aria-busy={loading}>
      <Header>
        <HeaderTitle>
          <TitleIcon aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={CLOSED_COLOR}
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="9" cy="8" r="4" />
              <path d="M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7" />
              <path d="M17 4a4 4 0 0 1 0 8" />
              <path d="M22 21c0-3-1.8-5.6-4.5-6.6" />
            </svg>
          </TitleIcon>
          <h2>Atendimentos por atendente</h2>
        </HeaderTitle>

        <HeaderActions>
          <MultiSelect
            appendTo="self"
            aria-label="Filtrar por atendente"
            optionLabel="label"
            optionValue="id"
            value={selectedUsers}
            options={options}
            onChange={(e) => setSelectedUsers(e.value ?? [])}
            placeholder="Todos"
            filter={true}
            filterPlaceholder="Buscar atendente"
            emptyFilterMessage="Nenhum atendente encontrado"
            maxSelectedLabels={1}
            selectedItemsLabel="{0} atendentes"
          />
          <div className="flex gap-2" role="group" aria-label="Ordenar por">
            <SortButton
              type="button"
              isActive={sort === "closed"}
              aria-pressed={sort === "closed"}
              onClick={() => setSort("closed")}
            >
              Mais encerrados
            </SortButton>
            <SortButton
              type="button"
              isActive={sort === "inProgress"}
              aria-pressed={sort === "inProgress"}
              onClick={() => setSort("inProgress")}
            >
              Mais em andamento
            </SortButton>
          </div>
        </HeaderActions>
      </Header>

      {!data ? (
        <Text color="#0A043C" s="1.25rem" lh="1.75rem" weight={600}>
          {loading ? "Carregando..." : "Sem dados"}
        </Text>
      ) : (
        <>
          <Tooltip
            target=".closed-in-period-info"
            position="top"
            event="both"
            style={{ maxWidth: "20rem" }}
          />
          <SummaryGrid>
            <SummaryCard>
              <span>Atendentes ativos</span>
              <SummaryValue>{formatNumber(summary?.activeAttendants)}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <span>
                Encerrados no período
                <InfoIcon
                  className="pi pi-info-circle closed-in-period-info"
                  data-pr-tooltip={CLOSED_IN_PERIOD_TOOLTIP}
                  aria-label={CLOSED_IN_PERIOD_TOOLTIP}
                  role="img"
                  tabIndex={0}
                />
              </span>
              <SummaryValue valueColor="#0350B8">
                {formatNumber(summary?.closedInPeriod)}
              </SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <span>Em andamento agora</span>
              <SummaryValue valueColor="#B85C00">
                {formatNumber(summary?.inProgressNow)}
              </SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <span>Média encerrada por atendente</span>
              <SummaryValue>
                {summary?.averageClosedPerAttendant === null ||
                summary?.averageClosedPerAttendant === undefined
                  ? "—"
                  : summary.averageClosedPerAttendant.toLocaleString("pt-BR", {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1
                    })}
              </SummaryValue>
            </SummaryCard>
          </SummaryGrid>

          {!sortedRows.length ? (
            <Text color="#0A043C" s="1.25rem" lh="1.75rem" weight={600}>
              Sem dados
            </Text>
          ) : (
            <>
              {chartRows.length > 0 && (
                <Chart>
                  <Legend>
                    <span>
                      <LegendColor swatch={CLOSED_COLOR} aria-hidden="true" />
                      Encerrados
                    </span>
                    <span>
                      <LegendColor
                        swatch={IN_PROGRESS_COLOR}
                        aria-hidden="true"
                      />
                      Em andamento
                    </span>
                  </Legend>
                  {chartRows.map((row) => {
                    const inProgress = row.inProgress ?? 0;
                    return (
                      <ChartRow key={row.userId}>
                        <span title={row.name}>{row.name}</span>
                        <BarTrack
                          role="img"
                          aria-label={`${row.name}: ${row.closed} encerrados, ${inProgress} em andamento`}
                        >
                          <BarSegment
                            barColor={CLOSED_COLOR}
                            barWidth={maxTotal ? (row.closed / maxTotal) * 100 : 0}
                          />
                          <BarSegment
                            barColor={IN_PROGRESS_COLOR}
                            barWidth={maxTotal ? (inProgress / maxTotal) * 100 : 0}
                          />
                        </BarTrack>
                        <span>{formatNumber(row.closed + inProgress)}</span>
                      </ChartRow>
                    );
                  })}
                </Chart>
              )}

              <TableWrapper>
                <table>
                  <thead>
                    <tr>
                      <th>Atendente</th>
                      <th>Em andamento</th>
                      <th>Encerrados</th>
                      <th>Assumidos</th>
                      <th>Transferidos</th>
                      <th>Taxa de encerramento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr key={row.system ? "system" : row.userId}>
                        <td>
                          <AttendantCell>
                            <Avatar isSystem={row.system} aria-hidden="true">
                              {initials(row.name)}
                            </Avatar>
                            {row.active ? row.name : `${row.name} (inativo)`}
                          </AttendantCell>
                        </td>
                        <NumberCell valueColor="#B85C00">
                          {formatNumber(row.inProgress)}
                        </NumberCell>
                        <NumberCell valueColor="#0350B8">
                          {formatNumber(row.closed)}
                        </NumberCell>
                        <NumberCell>{formatNumber(row.assumed)}</NumberCell>
                        <NumberCell>{formatNumber(row.transferred)}</NumberCell>
                        <td>
                          {row.closingRate === null ? (
                            "—"
                          ) : (
                            <RateCell>
                              <RateTrack aria-hidden="true">
                                <RateBar
                                  barWidth={Math.min(row.closingRate, 100)}
                                />
                              </RateTrack>
                              <span>{row.closingRate}%</span>
                            </RateCell>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrapper>
            </>
          )}
        </>
      )}

      <Footnote>
        Encerrados e assumidos consideram o período filtrado; “Em andamento”
        mostra a situação atual. Taxa de encerramento = encerrados ÷ assumidos.
      </Footnote>
    </Section>
  );
};

export default AttendantsProductivity;
